import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { clientPortalHomePath } from '../utils/clientPortalPaths';
import logoIcon from '../assets/logo-icon.png';

const API_BASE = '/api/v1/client-portal';

const ClientAccountPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken, isAuthenticated, isLoading, mustChangePassword, user, refreshUser } = useClientAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isLoading && mustChangePassword) {
      navigate('/client/change-password', { replace: true });
    }
  }, [isLoading, mustChangePassword, navigate]);

  if (!isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--background)] px-4 py-10">
        <div className="w-full max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-8 text-center shadow-[var(--portal-shadow)]">
          <p className="mb-4 text-sm text-[var(--muted-foreground)]">Sign in first to change your password while logged in.</p>
          <Link to="/client/login" className="text-sm font-semibold text-[var(--primary)] hover:underline">Go to sign in</Link>
          <p className="mt-4 text-sm text-[var(--muted-foreground)]">
            Forgot your password?{' '}
            <Link to="/client/forgot-password" className="text-[var(--primary)] hover:underline">Reset via email</Link>
          </p>
        </div>
      </div>
    );
  }

  if (!isLoading && mustChangePassword) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    const token = getToken();
    if (!token) {
      navigate('/client/login', { replace: true });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not change password.');
      }
      await refreshUser();
      setMessage('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--background)] px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <img src={logoIcon} alt="MyMoolah" className="h-10 w-10" />
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)]">MyMoolah Client Portal</p>
          <p className="text-xs text-[var(--muted-foreground)]">Change password</p>
        </div>
      </div>

      <div className="w-full max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--portal-shadow)]">
        <div className="mb-2 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          <h1 className="text-xl font-semibold text-[var(--foreground)]">Change your password</h1>
        </div>
        <p className="mb-6 text-sm text-[var(--muted-foreground)]">
          Signed in as <strong>{user?.email}</strong>. Enter your current password and choose a new one.
        </p>

        {error ? (
          <div className="mb-4 rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]" role="alert">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">
            {message}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="current" className="mb-1.5 block text-sm font-medium">Current password</label>
            <div className="relative">
              <input
                id="current"
                type={showPassword ? 'text' : 'password'}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] py-2.5 pl-4 pr-12 text-sm"
              />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-0 top-0 flex h-[44px] w-11 items-center justify-center text-[var(--muted-foreground)]" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="new" className="mb-1.5 block text-sm font-medium">New password</label>
            <input
              id="new"
              type={showPassword ? 'text' : 'password'}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm"
            />
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">Min 8 characters, one uppercase letter, one number</p>
          </div>
          <div>
            <label htmlFor="confirm" className="mb-1.5 block text-sm font-medium">Confirm new password</label>
            <input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm"
            />
          </div>
          <button type="submit" disabled={busy} className="min-h-[44px] w-full rounded-[var(--radius)] bg-[var(--primary)] text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-60">
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-sm">
          <button type="button" onClick={() => navigate(clientPortalHomePath(user))} className="inline-flex items-center gap-1 text-[var(--muted-foreground)] hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to portal
          </button>
          <Link to="/client/forgot-password" className="text-[var(--primary)] hover:underline">
            Forgot password? Reset via email
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientAccountPasswordPage;
